import React, {useMemo, useState} from "react";
import {
    Badge,
    Button,
    Flex,
    FormControl,
    FormLabel,
    HStack,
    Input,
    InputGroup,
    InputLeftElement,
    Select,
    SimpleGrid,
    Table,
    Tbody,
    Td,
    Text,
    Tfoot,
    Th,
    Thead,
    Tr,
    useColorModeValue,
    useDisclosure,
} from "@chakra-ui/react";
import Card from "components/Card/Card.js";
import CardBody from "components/Card/CardBody.js";
import CardHeader from "components/Card/CardHeader.js";
import {FiChevronLeft, FiChevronRight, FiSearch} from "react-icons/fi";
import AddSaleModal from "./AddSaleModal";
import {formatCurrency, PAYMENT_MODES, PAYMENT_STATUSES, saveSale} from "./Services";

const CAPTIONS = ["School", "Date", "Amount", "Discount", "VAT", "Total", "Status", "Mode", "Notes"];

const STATUS_COLORS = {
    PAID: "green",
    PENDING: "yellow",
    PARTIAL: "orange",
    FAILED: "red",
    REFUNDED: "purple",
};

const labelFor = (list, value) => (list.find((i) => i.value === value) || {}).label || value || "—";

function SalesList() {
    const textColor = useColorModeValue("gray.700", "white");
    const {isOpen, onOpen, onClose} = useDisclosure();

    // Local store until the backend is wired in (see Services.js stubs).
    const [sales, setSales] = useState([]);
    const [filters, setFilters] = useState({school: "", status: ""});
    const [page, setPage] = useState(1);
    const [size, setSize] = useState(10);

    const handleSaveSale = async (payload) => {
        const response = await saveSale(payload);
        if (response.status === 201 || response.status === 200) {
            setSales((current) => [response.data, ...current]);
            setPage(1);
        }
    };

    const handleFilterChange = (event) => {
        const {name, value} = event.target;
        setFilters((current) => ({...current, [name]: value}));
        setPage(1);
    };

    // Client-side filtering + pagination over the local list.
    const filtered = useMemo(() => {
        return sales.filter((s) => {
            const matchesSchool = !filters.school ||
                (s.schoolName || "").toLowerCase().includes(filters.school.toLowerCase());
            const matchesStatus = !filters.status || s.paymentStatus === filters.status;
            return matchesSchool && matchesStatus;
        });
    }, [sales, filters]);

    const totalItems = filtered.length;
    const totalPages = Math.max(1, Math.ceil(totalItems / size));
    const pageItems = filtered.slice((page - 1) * size, page * size);

    return (
        <Flex direction="column" pt={{base: "120px", md: "75px"}}>
            <Card overflowX={{sm: "scroll", xl: "hidden"}}>
                <CardHeader p="16px 0 24px 0">
                    <Flex direction="column" width="100%" gap="16px">
                        <Flex justify="space-between" align="center" wrap="wrap" gap="12px">
                            <Text fontSize="lg" color={textColor} fontWeight="semibold" flex="1" isTruncated>
                                Sales
                            </Text>
                            <Button colorScheme="blue" size="sm" onClick={onOpen}>
                                Add Sale
                            </Button>
                        </Flex>

                        {/* Filters */}
                        <SimpleGrid columns={{base: 1, md: 2}} spacing="12px" width="100%">
                            <FormControl>
                                <FormLabel fontSize="xs" color="gray.500" mb="4px">School</FormLabel>
                                <InputGroup size="sm">
                                    <InputLeftElement pointerEvents="none" children={<FiSearch color="gray"/>}/>
                                    <Input
                                        name="school"
                                        placeholder="Search by school"
                                        value={filters.school}
                                        onChange={handleFilterChange}
                                        borderRadius="8px"
                                    />
                                </InputGroup>
                            </FormControl>
                            <FormControl>
                                <FormLabel fontSize="xs" color="gray.500" mb="4px">Payment Status</FormLabel>
                                <Select
                                    name="status"
                                    size="sm"
                                    value={filters.status}
                                    onChange={handleFilterChange}
                                    placeholder="All statuses"
                                    borderRadius="8px"
                                >
                                    {PAYMENT_STATUSES.map((s) => (
                                        <option key={s.value} value={s.value}>{s.label}</option>
                                    ))}
                                </Select>
                            </FormControl>
                        </SimpleGrid>
                    </Flex>
                </CardHeader>

                <CardBody>
                    <Table variant="simple" color={textColor} size="sm" width="100%">
                        <Thead>
                            <Tr my=".8rem" color="gray.400">
                                {CAPTIONS.map((caption, idx) => (
                                    <Th color="gray.400" key={idx} ps={idx === 0 ? "0px" : null} fontSize="10px">
                                        {caption}
                                    </Th>
                                ))}
                            </Tr>
                        </Thead>
                        <Tbody>
                            {pageItems.map((sale) => (
                                <Tr key={sale.id}>
                                    <Td pl="0px">
                                        <Text fontSize="sm" fontWeight="bold" noOfLines={1}>{sale.schoolName}</Text>
                                        <Text fontSize="xs" color="gray.400">{sale.product}</Text>
                                    </Td>
                                    <Td><Text fontSize="sm">{sale.transactionDate}</Text></Td>
                                    <Td><Text fontSize="sm">{formatCurrency(sale.amount)}</Text></Td>
                                    <Td><Text fontSize="sm">{formatCurrency(sale.discount)}</Text></Td>
                                    <Td><Text fontSize="sm">{formatCurrency(sale.vatAmount)}</Text></Td>
                                    <Td><Text fontSize="sm" fontWeight="bold">{formatCurrency(sale.totalAmount)}</Text></Td>
                                    <Td>
                                        <Badge
                                            colorScheme={STATUS_COLORS[sale.paymentStatus] || "gray"}
                                            borderRadius="6px"
                                            px="8px"
                                            py="2px"
                                        >
                                            {labelFor(PAYMENT_STATUSES, sale.paymentStatus)}
                                        </Badge>
                                    </Td>
                                    <Td><Text fontSize="sm">{labelFor(PAYMENT_MODES, sale.paymentMode)}</Text></Td>
                                    <Td><Text fontSize="xs" color="gray.500" noOfLines={2} maxW="180px">{sale.notes || "—"}</Text></Td>
                                </Tr>
                            ))}
                            {totalItems === 0 && (
                                <Tr>
                                    <Td colSpan={CAPTIONS.length} py="40px" textAlign="center">
                                        <Text color="gray.500" fontSize="sm">
                                            No sales yet. Click “Add Sale” to record one.
                                        </Text>
                                    </Td>
                                </Tr>
                            )}
                        </Tbody>
                        <Tfoot>
                            <Tr>
                                <Td colSpan={CAPTIONS.length} px="0px" pt="16px" borderTopWidth="1px">
                                    <Flex justify="space-between" align="center" width="100%" gap="12px" wrap="wrap">
                                        <HStack spacing="12px">
                                            <Text color="gray.500" fontSize="sm">
                                                Page {page} of {totalPages} · {totalItems} sales
                                            </Text>
                                            <Select
                                                size="sm"
                                                width="auto"
                                                value={size}
                                                onChange={(event) => {
                                                    setPage(1);
                                                    setSize(Number(event.target.value));
                                                }}
                                            >
                                                {[10, 20, 50, 100].map((value) => (
                                                    <option key={value} value={value}>{value} / page</option>
                                                ))}
                                            </Select>
                                        </HStack>
                                        <HStack spacing="8px" justify="flex-end" wrap="wrap">
                                            <Button
                                                size="sm"
                                                leftIcon={<FiChevronLeft/>}
                                                onClick={() => setPage((current) => Math.max(1, current - 1))}
                                                isDisabled={page <= 1}
                                            >
                                                Previous
                                            </Button>
                                            <Button
                                                size="sm"
                                                rightIcon={<FiChevronRight/>}
                                                onClick={() => setPage((current) => Math.min(totalPages, current + 1))}
                                                isDisabled={page >= totalPages}
                                            >
                                                Next
                                            </Button>
                                        </HStack>
                                    </Flex>
                                </Td>
                            </Tr>
                        </Tfoot>
                    </Table>
                </CardBody>
            </Card>

            {isOpen && (
                <AddSaleModal isOpen={isOpen} onClose={onClose} onSubmit={handleSaveSale}/>
            )}
        </Flex>
    );
}

export default SalesList;
